import { z } from "zod";

/**
 * 締め請求書（#267 3a、docs/97。営業力 E3 → 現場力にも開く。門はモジュール sales）。
 *   請求の形＝{customer}の支払条件の「都度／締め」（既定 都度）。上書きの順は 伝票 → {project} → 工事 → {customer} → 都度、発行の瞬間に固める（DB の fn_resolve_billing_mode）。
 *   締めの{customer}は、締め日までの発行済・未請求の{deliveryNote}・{acceptance}（{deliveryNote}から作った双子は除く）を1枚の請求書に束ねる（1顧客1期間1枚）。
 *   番号は請求書と同じ通し番号・税は{customer}の「税を確定する書類」どおり（請求書＝明細から1回／{deliveryNote}＝足すだけ）
 */

/** 請求の形：per_delivery＝都度（既定）／closing＝締め */
export const BillingMode = z.enum(["per_delivery", "closing"]);
export type BillingMode = z.infer<typeof BillingMode>;
export const BILLING_MODE_LABEL: Record<BillingMode, string> = { per_delivery: "都度", closing: "締め" };

/** 明細の形（1a の BundleMode と同じ値。ここは zod だけに頼る＝estimate.ts から読んでも循環しない）：lines＝全行／perNote＝1枚1行 */
export const ClosingMode = z.enum(["lines", "perNote"]);
export type ClosingMode = z.infer<typeof ClosingMode>;

/** 締めの画面の月（YYYY-MM） */
export const ClosingMonth = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/);
export type ClosingMonth = z.infer<typeof ClosingMonth>;

/** {customer}の行の状態：未作成／下書き／発行済（締め請求書の状態） */
export const ClosingState = z.enum(["none", "draft", "issued"]);
export type ClosingState = z.infer<typeof ClosingState>;
export const CLOSING_STATE_LABEL: Record<ClosingState, string> = { none: "未作成", draft: "下書き", issued: "発行済" };

/** 締めの画面の{customer}の行（GET /closing?month=） */
export const ClosingRow = z.object({
  client: z.object({ id: z.string().uuid(), name: z.string(), honorific: z.string() }),
  /** 締め日（99＝末日） */
  closingDay: z.number().int(),
  periodStart: z.string(),
  periodEnd: z.string(),
  /** 締め日が来たか（今日が締め日より後か当日） */
  due: z.boolean(),
  /** まだ請求書に載っていない対象（締め日までの発行済・未請求。前の期間の出し忘れも） */
  count: z.number().int(),
  /** そのうち前の期間（期間の始めより前）の出し忘れ */
  prevCount: z.number().int(),
  subtotal: z.number().int(),
  total: z.number().int(),
  /** 税を確定する書類（invoice＝請求書／delivery＝{deliveryNote}） */
  taxOn: z.enum(["invoice", "delivery"]),
  state: ClosingState,
  /** この締め日の締め請求書（下書きも発行済も）。無ければ null */
  invoice: z.object({ id: z.string().uuid(), documentNo: z.string().nullable(), subtotal: z.number().int(), count: z.number().int() }).nullable(),
});
export type ClosingRow = z.infer<typeof ClosingRow>;

export const ClosingOverview = z.object({
  month: ClosingMonth,
  rows: z.array(ClosingRow),
  /** 明細の形の既定（会社の設定。既定は 1枚1行） */
  defaultMode: ClosingMode,
});
export type ClosingOverview = z.infer<typeof ClosingOverview>;

/** 行を開いたときの対象の書類（GET /closing/:clientId?month=） */
export const ClosingTarget = z.object({
  id: z.string().uuid(),
  kind: z.enum(["delivery", "acceptance"]),
  documentNo: z.string().nullable(),
  issueDate: z.string(),
  subject: z.string(),
  groupName: z.string().nullable(),
  subtotal: z.number().int(),
  total: z.number().int(),
  /** 前の期間の出し忘れ（発行日が期間の始めより前） */
  prev: z.boolean(),
});
export type ClosingTarget = z.infer<typeof ClosingTarget>;
export const ClosingTargets = z.object({ clientId: z.string().uuid(), periodStart: z.string(), periodEnd: z.string(), items: z.array(ClosingTarget) });
export type ClosingTargets = z.infer<typeof ClosingTargets>;

/** POST /closing/create：選んだ{customer}の締め請求書の下書きを作る。exclude＝「次の締めに回す」にした書類（保存しない） */
export const ClosingCreate = z.object({
  month: ClosingMonth,
  clients: z.array(z.object({ clientId: z.string().uuid(), exclude: z.array(z.string().uuid()).max(500).default([]) })).min(1).max(100),
  mode: ClosingMode.optional(),
});
export type ClosingCreate = z.infer<typeof ClosingCreate>;
export const ClosingCreateResult = z.object({
  created: z.array(z.object({ clientId: z.string().uuid(), invoiceId: z.string().uuid() })),
  failed: z.array(z.object({ clientId: z.string().uuid(), clientName: z.string(), code: z.string(), message: z.string() })),
});
export type ClosingCreateResult = z.infer<typeof ClosingCreateResult>;

/** POST /closing/separate：締めの対象を「別請求にする」（伝票の請求の形を都度に。発行のあとでも） */
export const ClosingSeparate = z.object({ documentId: z.string().uuid() });
export type ClosingSeparate = z.infer<typeof ClosingSeparate>;

/** 請求書の締め（Invoice.closing）：期間と束ねた書類の数・明細の形 */
export const InvoiceClosing = z.object({
  periodStart: z.string(),
  periodEnd: z.string(),
  notes: z.number().int(),
  acceptances: z.number().int(),
  closingDay: z.number().int(),
  mode: ClosingMode,
});
export type InvoiceClosing = z.infer<typeof InvoiceClosing>;

/** ホームの「締め n社（{month}月分）」（v_closing_due）。締め日が来て未請求の対象がある{customer}の数。0社なら省く */
export const HomeClosingDue = z.object({ count: z.number().int(), month: ClosingMonth });
export type HomeClosingDue = z.infer<typeof HomeClosingDue>;

const md = (d: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
  return m ? { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) } : null;
};

/** 締め請求書の件名の既定（期間の終わりの月）：「2026年10月分 ご請求」 */
export function closingSubject(periodEnd: string): string {
  const e = md(periodEnd);
  return e ? `${e.y}年${e.m}月分 ご請求` : "ご請求";
}

/** 期間の表示：「2026年10月1日〜10月31日分」（年をまたぐときは終わりにも年） */
export function closingPeriodLabel(start: string, end: string): string {
  const s = md(start);
  const e = md(end);
  if (!s || !e) return `${start}〜${end}分`;
  return `${s.y}年${s.m}月${s.d}日〜${e.y !== s.y ? `${e.y}年` : ""}${e.m}月${e.d}日分`;
}

/** 締め日の表示：99＝末日 → 「末日」、ほかは「20日」 */
export function closingDayLabel(day: number): string {
  return day >= 99 ? "末日" : `${day}日`;
}

/** 月（YYYY-MM）を n か月ずらす */
export function shiftMonth(month: string, n: number): string {
  const [y, m] = month.split("-").map(Number) as [number, number];
  const t = y * 12 + (m - 1) + n;
  return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, "0")}`;
}

/** 月（YYYY-MM）の表示：「2026年10月分」 */
export function closingMonthLabel(month: string): string {
  const [y, m] = month.split("-").map(Number);
  return `${y}年${m}月分`;
}

/**
 * 締めの画面・請求の形の文（{customer}・{project}・{deliveryNote}・{acceptance} は画面が t()、API が fillLabels で埋める）。
 * ボタンに「を」「の」を入れない・見出しは名詞だけ（docs/10 §5）
 */
export const CLOSING_MSG = {
  title: "締め請求書",
  back: "請求書一覧",
  /** 行・まとめて操作のボタン */
  create: "下書き作成",
  createAll: "まとめて作成",
  deselect: "選択解除",
  exclude: "外す",
  nextClosing: "次の締めに回す",
  nextClosingNote: (month: number) => `${month}月分の締めで対象に戻ります`,
  undoExclude: "戻す",
  separate: "別請求にする",
  separateNote: "締めに入れず、この書類だけで請求します",
  /** 「前の期間」のチップ（出し忘れ） */
  prev: "前の期間",
  prevCount: (n: number) => `前の期間 ${n}`,
  selected: (n: number) => `${n}社 選択`,
  /** 表の見出し */
  colClient: "{customer}",
  colClosingDay: "締め日",
  colPeriod: "期間",
  colCount: "件数",
  colAmount: "金額（税抜）",
  colState: "状態",
  colDate: "日付",
  colKind: "書類",
  colNo: "番号",
  colSubject: "件名",
  colGroup: "{project}",
  /** 開いた行の下 */
  targets: (n: number) => `対象 ${n}件`,
  mode: "明細の形",
  /** 設定 → 見積・請求 の行（会社の既定） */
  closingModeLabel: "締め請求書の明細",
  /** {customer}の行の2行目 */
  taxOnDelivery: "税は{deliveryNote}で確定",
  /** 表の下 */
  footer: (clients: number, count: number) => `${clients}社　対象 ${count}件`,
  empty: "この月の締めの対象はありません",
  emptyHelp: "締めの{customer}は、{customer}の支払条件の「請求方式」で設定します",
  help:
    "締め日までに発行した{deliveryNote}・{acceptance}を、{customer}ごとに1枚の請求書にまとめます。前の期間の未請求分も入ります。" +
    "「外す」の「次の締めに回す」は今回だけ外し、「別請求にする」はその書類だけで請求します。",
  /** 通知 */
  created: (n: number) => `請求書の下書きを ${n}件 作成しました`,
  createdOne: "請求書の下書きを作成しました",
  failedSome: (n: number) => `${n}件 作成できませんでした`,
  separated: "別請求にしました",
  /** API の断り */
  notFound: "その{customer}の締めの対象は見つかりません",
  noTargets: "締めの対象がありません",
  exists: "この締め日の請求書は作成済みです",
  taxMixed:
    "{deliveryNote}で税を確定する{customer}の締めに、適格請求書でない書類（{acceptance}・登録番号のない{deliveryNote}）が入っています。外してから作成してください",
  notTarget: "締めの対象でない書類です",
  /** 請求書の画面の締めの帯 */
  band: "締め",
  bandDocs: (notes: number, acceptances: number) =>
    [notes ? `{deliveryNote} ${notes}件` : "", acceptances ? `{acceptance} ${acceptances}件` : ""].filter(Boolean).join("・"),
  /** 紙の明細表の上の表（docs/97 決めごと6）：見出しと値 */
  paperHeads: ["ご請求期間", "対象"] as const,
  /** 請求書の画面の3段目の「締め」の値 */
  monthLink: (month: number) => `${month}月分 ›`,
  /** ホーム「未請求」のカードの行 */
  homeDue: (n: number, month: number) => `締め ${n}社（${month}月分）`,
  /** 請求方式（{customer}の支払条件の窓・{project}。#280 で「請求の形」から） */
  billingMode: "請求方式",
  billingModeHelp:
    "締め＝締め日までに発行した{deliveryNote}・{acceptance}を1枚の請求書にまとめます。都度＝受注・{deliveryNote}ごとに請求書を作成します。",
  groupFollow: (label: string) => `{customer}の設定（${label}）`,
  groupFollowSrc: "（{customer}の設定）",
  groupOwnSrc: "（この{project}だけ）",
  groupHelp: "この{project}だけ請求方式を変えます。締めの{customer}でも、この{project}の{deliveryNote}は都度で請求できます",
  /** {deliveryNote}の発行の確認 */
  noClosing: "締めに入れない",
  noClosingHelp: "この{deliveryNote}だけ締めに入れず、別に請求します",
} as const;
